import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Row,
  Col,
  Button,
  Form,
  Dropdown,
  ButtonGroup,
  Modal,
  Card,
  Alert,
} from "react-bootstrap";
import { MdAddchart } from "react-icons/md";
import { Link } from "react-router-dom";
import "./emp-info.css";
import { BsFillInfoCircleFill } from "react-icons/bs";
import { CgProfile } from "react-icons/cg";
import { FcCancel } from "react-icons/fc";
import { RiDeleteBin5Line } from "react-icons/ri";
import { FaEdit, FaDownload, FaFileUpload } from "react-icons/fa";
import { IoIosEye } from "react-icons/io";

export default function ExEmployeeInformation() {
  const rowsPerPage = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [employeeToDelete, setEmployeeToDelete] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showClearanceModal, setShowClearanceModal] = useState(false);
  const [documentUrl, setDocumentUrl] = useState("");
  const [alert, setAlert] = useState({
    show: false,
    message: "",
    variant: "success",
  });

  useEffect(() => {
    const fetchEmployees = async () => {
      setLoading(true);
      try {
        const response = await axios.get(
          "https://backend.ems.hiradentallab.com/api/user/getAllUsers"
        );

        const inactiveUsers = response.data
          .filter((user) => user.status === "inactive")
          .sort((a, b) => a.name.localeCompare(b.name));

        setData(inactiveUsers);
        setSearchResults(inactiveUsers);
      } catch (error) {
        console.error("Error fetching employees:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchEmployees();
  }, []);

  useEffect(() => {
    const fetchSearchResults = async () => {
      if (searchQuery.trim() === "") {
        setSearchResults(data);
        return;
      }

      try {
        const response = await axios.get(
          `https://backend.ems.hiradentallab.com/api/user/search?query=${searchQuery}`
        );
        const inactiveSearchResults = response.data
          .filter((user) => user.status === "inactive")
          .sort((a, b) => a.name.localeCompare(b.name));
        setSearchResults(inactiveSearchResults);
      } catch (err) {
        console.error("Error fetching search results:", err);
        setSearchResults([]);
      }
    };

    const timeoutId = setTimeout(fetchSearchResults, 500);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, data]);

  const showAlert = (message, variant = "success") => {
    setAlert({ show: true, message, variant });
    setTimeout(
      () => setAlert({ show: false, message: "", variant: "success" }),
      3000
    );
  };

  const handlePageChange = (pageNumber) => {
    if (
      pageNumber >= 1 &&
      pageNumber <= Math.ceil(searchResults.length / rowsPerPage)
    ) {
      setCurrentPage(pageNumber);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
  };

  const pageRange = () => {
    const pages = [];
    const totalPages = Math.ceil(searchResults.length / rowsPerPage);
    let startPage = Math.max(currentPage - 1, 1);
    let endPage = Math.min(startPage + 2, totalPages);

    if (endPage - startPage < 2) {
      startPage = Math.max(endPage - 2, 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  };

  const handleShow = (employeeData) => {
    setSelectedEmployee(employeeData);
    setShowDialog(true);
  };

  const handleClose = () => {
    setShowDialog(false);
    setSelectedEmployee(null);
  };

  const handleDeleteModal = (employee) => {
    setEmployeeToDelete(employee);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirmed = async () => {
    try {
      await axios.delete(
        `https://backend.ems.hiradentallab.com/api/user/delete/${employeeToDelete.userId}`
      );
      setData(
        data.filter((employee) => employee.userId !== employeeToDelete.userId)
      );
      setSearchResults(
        searchResults.filter(
          (employee) => employee.userId !== employeeToDelete.userId
        )
      );
      setShowDeleteModal(false);
      showAlert("Employee deleted successfully!");
    } catch (error) {
      console.error("Error deleting employee:", error);
      showAlert("Error deleting employee", "danger");
    }
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setEmployeeToDelete(null);
  };

  const handleClearanceUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("clearanceDocument", file);

    try {
      const response = await axios.put(
        `https://backend.ems.hiradentallab.com/api/user/uploadClearance/${selectedEmployee.userId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      const updatedEmployee = response.data.updatedUser;
      setSelectedEmployee(updatedEmployee);
      setData(
        data.map((emp) =>
          emp.userId === updatedEmployee.userId ? updatedEmployee : emp
        )
      );
      setSearchResults(
        searchResults.map((emp) =>
          emp.userId === updatedEmployee.userId ? updatedEmployee : emp
        )
      );
      showAlert("Clearance document uploaded successfully!");
    } catch (error) {
      console.error("Error uploading clearance document:", error);
      showAlert("Error uploading clearance document", "danger");
    }
  };

  const handleShowClearanceModal = () => {
    if (selectedEmployee.clearanceDocument) {
      setDocumentUrl(
        `https://backend.ems.hiradentallab.com/${selectedEmployee.clearanceDocument}`
      );
      setShowClearanceModal(true);
      setShowDialog(false);
    }
  };

  const handleCloseClearanceModal = () => {
    setShowClearanceModal(false);
    setDocumentUrl("");
  };

  const handleReplaceClearance = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("clearanceDocument", file);

    try {
      const response = await axios.put(
        `https://backend.ems.hiradentallab.com/api/user/uploadClearance/${selectedEmployee.userId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      const updatedEmployee = response.data.updatedUser;
      setSelectedEmployee(updatedEmployee);
      setData(
        data.map((emp) =>
          emp.userId === updatedEmployee.userId ? updatedEmployee : emp
        )
      );
      setSearchResults(
        searchResults.map((emp) =>
          emp.userId === updatedEmployee.userId ? updatedEmployee : emp
        )
      );
      handleCloseClearanceModal();
      showAlert("Clearance document replaced successfully!");
    } catch (error) {
      console.error("Error replacing clearance document:", error);
      showAlert("Error replacing clearance document", "danger");
    }
  };

  const downloadFile = (url, filename) => {
    fetch(url)
      .then((response) => response.blob())
      .then((blob) => {
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = filename || `${selectedEmployee.name}_Clearance`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(blobUrl);
      })
      .catch((error) => console.error("Error downloading file:", error));
  };

  const renderPreview = (url) => {
    const ext = getFileExtension(url);

    if (ext === "pdf") {
      return (
        <iframe src={url} title="PDF Preview" className="pdf-viewer"></iframe>
      );
    }

    if (["jpg", "jpeg", "png", "gif"].includes(ext)) {
      return <img src={url} alt="Preview" />;
    }

    return (
      <div>
        <a href={url} target="_blank" rel="noopener noreferrer">
          Download File
        </a>
      </div>
    );
  };

  const getFileExtension = (url) => {
    return url.split(".").pop().toLowerCase();
  };

  return (
    <div className="container-fluid">
      {alert.show && (
        <Alert variant={alert.variant} className="alert-toast">
          {alert.message}
        </Alert>
      )}

      <h1 className="emp-info-title">
        <BsFillInfoCircleFill className="settings-icon" /> Ex-Employee
        Information
      </h1>
      <hr />
      <h1 className="emp-info-subtitle">
        <CgProfile className="settings-icon" /> Ex-Employee
      </h1>
      <hr />
      <div className="emp-info-content">
        <Link to="/add-emp">
          <Button variant="primary" className="add-emp-button">
            <MdAddchart className="button-icon" /> Add Employee
          </Button>
        </Link>

        <div className="emp-info-actions">
          <Row>
            <Col xs={12} md={6} className="d-flex align-items-center mb-2">
              <Dropdown as={ButtonGroup}>
                <Button variant="primary">Show Entries</Button>
                <Dropdown.Toggle
                  split
                  variant="primary"
                  id="dropdown-split-basic"
                />
                <Dropdown.Menu>
                  <Dropdown.Item href="#/action-1">10</Dropdown.Item>
                  <Dropdown.Item href="#/action-2">25</Dropdown.Item>
                  <Dropdown.Item href="#/action-3">50</Dropdown.Item>
                  <Dropdown.Item href="#/action-4">100</Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </Col>

            <Col xs={12} md={6} className="d-flex justify-content-end mb-2">
              <div className="search-container">
                <Form className="d-flex" onSubmit={handleSubmit}>
                  <Form.Control
                    id="searchQuery"
                    type="text"
                    className="form-control"
                    placeholder="Search Ex-Employee"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </Form>
              </div>
            </Col>
          </Row>
        </div>

        <div className="table-responsive">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <table className="table table-striped table-style">
              <thead className="tablehead">
                <tr className="table-header-footer">
                  <th className="text-center">SR</th>
                  <th className="text-center">User ID</th>
                  <th className="text-center">Employee Name</th>
                  <th className="text-center">Department</th>
                  <th className="text-center">Job Title</th>
                  <th className="text-center">Email</th>
                  <th className="text-center">Gender</th>
                  <th className="text-center">Status</th>
                  <th className="text-center">Salary</th>
                  <th className="text-center">Action</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="10">Loading...</td>
                  </tr>
                ) : searchResults.length > 0 ? (
                  searchResults
                    .slice(
                      (currentPage - 1) * rowsPerPage,
                      currentPage * rowsPerPage
                    )
                    .map((employee, index) => (
                      <tr key={employee._id} style={{ textAlign: "center" }}>
                        <td>{(currentPage - 1) * rowsPerPage + index + 1}</td>
                        <td>{employee.userId}</td>
                        <td>{employee.name}</td>
                        <td>{employee.department?.name || "N/A"}</td>
                        <td>{employee.jobTitle || "N/A"}</td>
                        <td>{employee.email}</td>
                        <td>{employee.gender}</td>
                        <td>
                          <FcCancel />
                        </td>
                        <td>{employee.salary}</td>
                        <td>
                          <div>
                            <Button
                              variant="primary"
                              style={{ marginRight: "5px" }}
                              onClick={() => handleShow(employee)}
                              size="sm"
                            >
                              <IoIosEye />
                            </Button>

                            <Link to={`/edit-emp/${employee.userId}`}>
                              <Button
                                style={{ marginRight: "5px" }}
                                variant="warning"
                                size="sm"
                              >
                                <FaEdit />
                              </Button>
                            </Link>

                            <Button
                              variant="danger"
                              onClick={() => handleDeleteModal(employee)}
                              size="sm"
                            >
                              <RiDeleteBin5Line />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                ) : (
                  <tr>
                    <td colSpan="10">No data available</td>
                  </tr>
                )}
              </tbody>
              <tfoot className="table-footer">
                <tr className="emp-info-table-footer">
                  <td>Total</td>
                  <td>-</td>
                  <td>{searchResults.length}</td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          )}
          <Row className="mt-2">
            <Col xs={12} md={6} className="d-flex align-items-center">
              <p className="showing-entries">
                Showing {(currentPage - 1) * rowsPerPage + 1} to{" "}
                {Math.min(currentPage * rowsPerPage, searchResults.length)} of{" "}
                {searchResults.length} entries
              </p>
            </Col>
            <Col xs={12} md={6} className="d-flex justify-content-end">
              <nav>
                <ul className="pagination">
                  <li
                    className={`page-item ${
                      currentPage === 1 ? "disabled" : ""
                    }`}
                  >
                    <a
                      href="/#"
                      className="page-link"
                      onClick={(e) => {
                        e.preventDefault();
                        handlePageChange(currentPage - 1);
                      }}
                    >
                      Previous
                    </a>
                  </li>
                  {pageRange().map((page) => (
                    <li
                      key={page}
                      className={`page-item ${
                        currentPage === page ? "active" : ""
                      }`}
                    >
                      <a
                        href="/#"
                        className="page-link"
                        onClick={(e) => {
                          e.preventDefault();
                          handlePageChange(page);
                        }}
                      >
                        {page}
                      </a>
                    </li>
                  ))}
                  <li
                    className={`page-item ${
                      currentPage ===
                      Math.ceil(searchResults.length / rowsPerPage)
                        ? "disabled"
                        : ""
                    }`}
                  >
                    <a
                      href="/#"
                      className="page-link"
                      onClick={(e) => {
                        e.preventDefault();
                        handlePageChange(currentPage + 1);
                      }}
                    >
                      Next
                    </a>
                  </li>
                </ul>
              </nav>
            </Col>
          </Row>
        </div>
      </div>

      {/* Modal for displaying employee details */}
      {selectedEmployee && (
        <Modal
          show={showDialog}
          onHide={handleClose}
          className="custom-modal"
          size="lg"
        >
          <Modal.Header
            style={{ backgroundColor: "#004aad", color: "#ffffff" }}
          >
            <Modal.Title style={{ color: "#ffffff" }}>
              Ex-Employee Details
            </Modal.Title>
            <button
              onClick={handleClose}
              style={{
                color: "white",
                background: "transparent",
                border: "none",
                fontSize: "50px",
                marginLeft: "50%",
              }}
            >
              ×
            </button>
          </Modal.Header>

          <Modal.Body>
            <Row>
              <Col md={8}>
                <div className="profile-photo-container">
                  <h5>Profile Photo</h5>
                  {selectedEmployee.profilePhoto ? (
                    <img
                      src={`https://backend.ems.hiradentallab.com/${selectedEmployee.profilePhoto}`}
                      alt="Employee Photo"
                      className="employee-photo rounded-photo"
                    />
                  ) : (
                    <p>No profile photo available.</p>
                  )}
                </div>
                <div className="employee-info">
                  <p>
                    <strong>Name:</strong> {selectedEmployee.name}
                  </p>
                  <p>
                    <strong>Role:</strong> {selectedEmployee.role}
                  </p>
                  <p>
                    <strong>User ID:</strong> {selectedEmployee.userId}
                  </p>
                  <p>
                    <strong>User Name:</strong> {selectedEmployee.username}
                  </p>
                  <p>
                    <strong>Email:</strong> {selectedEmployee.email}
                  </p>
                  <p>
                    <strong>Department:</strong>{" "}
                    {selectedEmployee.department?.name || "N/A"}
                  </p>
                  <p>
                    <strong>Job Title:</strong>{" "}
                    {selectedEmployee.jobTitle || "N/A"}
                  </p>
                  <p>
                    <strong>Salary:</strong> {selectedEmployee.salary}
                  </p>
                  <p>
                    <strong>Gender:</strong> {selectedEmployee.gender}
                  </p>
                  <p>
                    <strong>Address:</strong> {selectedEmployee.address}
                  </p>
                  <p>
                    <strong>Status:</strong> {selectedEmployee.status}
                  </p>
                  <p>
                    <strong>Marital Status:</strong>{" "}
                    {selectedEmployee.maritalstatus}
                  </p>
                  <p>
                    <strong>Join Date:</strong>{" "}
                    {new Date(selectedEmployee.joinDate).toLocaleDateString()}
                  </p>
                  <p>
                    <strong>Birthday According to CNIC:</strong>{" "}
                    {new Date(selectedEmployee.birthday).toLocaleDateString()}
                  </p>
                  <p>
                    <strong>Work Description:</strong>{" "}
                    {selectedEmployee.workDescription}
                  </p>
                  <p>
                    <strong>Work Experience:</strong>{" "}
                    {selectedEmployee.workExperience}
                  </p>
                </div>
              </Col>
            </Row>

            <hr />

            <div className="documents-section">
              <h5 className="mb-3">Employee Documents</h5>
              <Row>
                <Col md={6} className="mb-3">
                  <Card className="custom-card">
                    <Card.Body>
                      <h6>Personal Documents</h6>
                      {selectedEmployee.cv ? (
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() =>
                            downloadFile(
                              `https://backend.ems.hiradentallab.com/${selectedEmployee.cv}`,
                              `${selectedEmployee.name}_CV`
                            )
                          }
                        >
                          <FaDownload title="Download" />
                        </Button>
                      ) : (
                        <p className="text-muted mb-0">
                          No personal documents available.
                        </p>
                      )}
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={6} className="mb-3">
                  <Card className="custom-card">
                    <Card.Body>
                      <h6>Health and Safety Certificates</h6>
                      {selectedEmployee.idCardFront ? (
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() =>
                            downloadFile(
                              `https://backend.ems.hiradentallab.com/${selectedEmployee.idCardFront}`,
                              `${selectedEmployee.name}_Certificate`
                            )
                          }
                        >
                          <FaDownload title="Download" />
                        </Button>
                      ) : (
                        <p className="text-muted mb-0">
                          No certificate available.
                        </p>
                      )}
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={6} className="mb-3">
                  <Card className="custom-card">
                    <Card.Body>
                      <h6>Handbook and Employment Contracts</h6>
                      {selectedEmployee.idCardBack ? (
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() =>
                            downloadFile(
                              `https://backend.ems.hiradentallab.com/${selectedEmployee.idCardBack}`,
                              `${selectedEmployee.name}_Contracts`
                            )
                          }
                        >
                          <FaDownload title="Download" />
                        </Button>
                      ) : (
                        <p className="text-muted mb-0">
                          No contracts available.
                        </p>
                      )}
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={6} className="mb-3">
                  <Card className="custom-card">
                    <Card.Body>
                      <h6>Company Data Protection and Other Contracts</h6>
                      {selectedEmployee.pastWorkCertificate ? (
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() =>
                            downloadFile(
                              `https://backend.ems.hiradentallab.com/${selectedEmployee.pastWorkCertificate}`,
                              `${selectedEmployee.name}_Company_Contracts`
                            )
                          }
                        >
                          <FaDownload title="Download" />
                        </Button>
                      ) : (
                        <p className="text-muted mb-0">
                          No company contracts available.
                        </p>
                      )}
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={6} className="mb-3">
                  <Card className="custom-card clearance-card">
                    <Card.Body className="text-center">
                      <h6>Clearance Document</h6>
                      {selectedEmployee.clearanceDocument ? (
                        <Button
                          variant="primary"
                          className="fancy-btn"
                          onClick={handleShowClearanceModal}
                        >
                          <IoIosEye />
                        </Button>
                      ) : (
                        <div>
                          <p className="text-muted mb-2">
                            No clearance certificate available.
                          </p>
                          <Form.Group>
                            <Form.Label>
                              Upload Clearance Certificate
                            </Form.Label>
                            <div className="d-flex align-items-center">
                              <label
                                htmlFor="upload-clearance"
                                className="btn btn-primary fancy-upload-btn"
                              >
                                <FaFileUpload className="me-2" /> Choose File
                              </label>
                              <input
                                type="file"
                                id="upload-clearance"
                                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
                                onChange={handleClearanceUpload}
                                style={{ display: "none" }}
                              />
                            </div>
                          </Form.Group>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </div>
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={handleClose}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {/* Modal for viewing clearance document */}
      {selectedEmployee && showClearanceModal && (
        <Modal
          show={showClearanceModal}
          onHide={handleCloseClearanceModal}
          size="xl"
          centered
          className="clearance-modal"
        >
          {/* Close button */}
          <button
            className="clearance-close"
            onClick={handleCloseClearanceModal}
          >
            ×
          </button>

          {/* Preview */}
          <Modal.Body className="document-modal-body">
            <div className="document-viewer">{renderPreview(documentUrl)}</div>
          </Modal.Body>

          {/* Footer */}
          <Modal.Footer>
            <Button
              variant="primary"
              as="label"
              htmlFor="modal-replace-clearance"
            >
              Replace
            </Button>
            <input
              type="file"
              id="modal-replace-clearance"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
              onChange={handleReplaceClearance}
              style={{ display: "none" }}
            />
            <Button
              variant="success"
              onClick={() =>
                downloadFile(
                  documentUrl,
                  `${selectedEmployee.name}_Clearance.${getFileExtension(
                    documentUrl
                  )}`
                )
              }
            >
              Download
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
}
